import { useId, useMemo, useState } from 'react'

import type { Subject, SubjectOf } from '../api/subjects'

/**
 * The one control that puts something under a subject — used at a document, a folder, a note, a
 * flashcard, a task and a board alike.
 *
 * The interaction is taken from the flashcards' tag field: a combobox, suggestions while typing,
 * chips that can be removed, everything reachable from the keyboard. **One difference is deliberate**
 * and is the whole point of not simply reusing that component: a tag comes into being in passing
 * while somebody types, and a subject must not. Typing a name that does not exist offers
 * *"Create subject …"* as a step of its own rather than quietly making one — subjects are few and
 * permanent, and a typo that silently produced an eighth subject would be exactly the disorder this
 * area was built against.
 *
 * The second difference is [SubjectOf.inherited]. A subject a piece of content has through the folder
 * it lies in is shown as such and **cannot be removed here**: it is a statement about that folder,
 * and offering a control that pretended otherwise would leave somebody clicking at something that
 * cannot change. The chip says where it came from instead.
 */
export interface SubjectPickerProps {
  /** What this piece of content already belongs to, direct and inherited alike. */
  readonly chosen: readonly SubjectOf[]
  /** Every subject there is to choose from. Archived ones are filtered out by the caller. */
  readonly known: readonly Subject[]
  /** Exactly one subject, as an appointment has. The chosen one is replaced rather than added to. */
  readonly single?: boolean
  readonly label?: string
  readonly onFile: (subjectId: string) => void
  readonly onUnfile: (subjectId: string) => void
  /** Makes a subject of this name and files it. Absent where creating one is not offered. */
  readonly onCreate?: (name: string) => void
  readonly disabled?: boolean
}

interface Option {
  readonly id: string
  readonly name: string
  readonly isNew: boolean
}

export function SubjectPicker({
  chosen,
  known,
  single = false,
  label = 'Subjects',
  onFile,
  onUnfile,
  onCreate,
  disabled = false,
}: SubjectPickerProps) {
  const listId = useId()
  const fieldId = useId()
  const hintId = useId()
  const [typed, setTyped] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)

  const options = useMemo(
    () => suggestions(typed, chosen, known, onCreate !== undefined),
    [typed, chosen, known, onCreate],
  )
  const expanded = open && options.length > 0
  const activeOption = options[Math.min(active, Math.max(0, options.length - 1))]

  const take = (option: Option) => {
    if (option.isNew) {
      onCreate?.(option.name)
    } else {
      // With one subject only, taking a second one means replacing the first: an appointment is about one
      // thing, and two chips on it would be a lie the grid could not draw either.
      if (single) {
        chosen
          .filter((one) => !one.inherited)
          .forEach((one) => {
            onUnfile(one.id)
          })
      }
      onFile(option.id)
    }
    setTyped('')
    setActive(0)
  }

  return (
    <div className="subject-field">
      <label className="subject-field__label" htmlFor={fieldId}>
        {label}
      </label>
      <div className="subject-field__combobox">
        <div className="subject-field__input">
          {chosen.map((one) =>
            one.inherited ? (
              /*
                Inherited, and therefore not removable here. It says where it came from instead of
                offering a control that would do nothing.
              */
              <span className="subject subject--inherited" key={one.id} title="From a folder">
                <span>{one.name}</span>
                <span className="subject__from">from a folder</span>
              </span>
            ) : (
              <button
                type="button"
                tabIndex={-1}
                className="subject subject--removable"
                aria-label={`Remove subject: ${one.name}`}
                key={one.id}
                disabled={disabled}
                onClick={() => {
                  onUnfile(one.id)
                }}
              >
                <span>{one.name}</span>
                <span aria-hidden="true">×</span>
              </button>
            ),
          )}
          <input
            id={fieldId}
            role="combobox"
            className="subject-field__typing"
            value={typed}
            disabled={disabled}
            placeholder={chosen.length === 0 ? 'Find a subject' : 'Another subject'}
            aria-autocomplete="list"
            aria-expanded={expanded}
            aria-controls={listId}
            aria-activedescendant={
              expanded && activeOption !== undefined ? `${listId}-${active}` : undefined
            }
            aria-describedby={hintId}
            onFocus={() => {
              setOpen(true)
            }}
            onBlur={() => {
              setOpen(false)
            }}
            onChange={(event) => {
              setTyped(event.target.value)
              setActive(0)
              setOpen(true)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' && options.length > 0) {
                event.preventDefault()
                setOpen(true)
                setActive((before) => (before + 1) % options.length)
              } else if (event.key === 'ArrowUp' && options.length > 0) {
                event.preventDefault()
                setOpen(true)
                setActive((before) => (before - 1 + options.length) % options.length)
              } else if (event.key === 'Enter' && expanded && activeOption !== undefined) {
                /*
                  Enter takes what is highlighted, and nothing else. Unlike a tag field it never
                  creates from the raw text on its own: making a subject is a step somebody chooses.
                */
                event.preventDefault()
                take(activeOption)
              } else if (event.key === 'Escape') {
                event.preventDefault()
                setOpen(false)
              }
            }}
          />
        </div>
        {expanded && (
          <ul
            className="subject-field__options"
            id={listId}
            role="listbox"
            aria-label="Subject suggestions"
          >
            {options.map((option, index) => (
              <li
                id={`${listId}-${index}`}
                role="option"
                aria-label={`${option.name}, ${option.isNew ? 'Create subject' : 'Existing subject'}`}
                aria-selected={index === active}
                className="subject-field__option"
                key={option.isNew ? `new-${option.name}` : option.id}
                onMouseDown={(event) => {
                  event.preventDefault()
                }}
                onClick={() => {
                  take(option)
                }}
              >
                <span>{option.isNew ? `Create subject “${option.name}”` : option.name}</span>
                <small>{option.isNew ? 'New' : 'Subject'}</small>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="subject-field__hint" id={hintId}>
        {single
          ? 'One subject or none. Choosing another replaces the one that is set.'
          : 'A subject says what this is about. A folder says where it lies — the two are different things.'}
      </p>
    </div>
  )
}

function suggestions(
  typed: string,
  chosen: readonly SubjectOf[],
  known: readonly Subject[],
  canCreate: boolean,
): Option[] {
  const query = typed.trim().toLocaleLowerCase()
  const taken = chosen.map((one) => one.id)
  const matching = known
    .filter((subject) => !taken.includes(subject.id))
    .filter((subject) => query === '' || subject.name.toLocaleLowerCase().includes(query))
    .map((subject) => ({ id: subject.id, name: subject.name, isNew: false }))

  const exists = known.some((subject) => subject.name.toLocaleLowerCase() === query)
  if (!canCreate || query === '' || exists) {
    return matching
  }
  return [...matching, { id: '', name: typed.trim(), isNew: true }]
}
